<template>
  <v-navigation-drawer
    :value="isAddNewUserSidebarActive"
    temporary
    touchless
    :right="!$vuetify.rtl"
    :width="$vuetify.breakpoint.mdAndUp ? '40%' : '100%'"
    app
    @input="(val) => $emit('update:is-add-new-user-sidebar-active', val)"
  >
    <div class="drawer-header d-flex align-center">
      <span v-t="'addNewHouse'" class="font-weight-semibold text-base text--primary" />
      <v-spacer></v-spacer>
      <v-btn icon small @click="$emit('update:is-add-new-user-sidebar-active', false)">
        <v-icon size="22">
          {{ icons.mdiClose }}
        </v-icon>
      </v-btn>
    </div>
    <v-form ref="form" v-model="valid" @submit.prevent="onSubmit">
      <v-card-text>
        <v-subheader v-t="'basicData'" />
        <v-text-field
          v-model="houseData.name"
          outlined
          dense
          :rules="[validators.required]"
          :label="$t('name')"
          :placeholder="$t('name')"
          hide-details="auto"
          maxlength="150"
          class="mb-3"
        ></v-text-field>

        <v-textarea
          v-model="houseData.description"
          outlined
          auto-grow
          :label="$t('description')"
          :placeholder="$t('description')"
          rows="4"
          maxlength="1000"
          hide-details="auto"
          class="mb-3"
        />
        <v-autocomplete
          v-model="houseData.status"
          :label="$t('status', { postfix: '' })"
          :placeholder="$t('status', { postfix: '' })"
          :items="statusChoices"
          outlined
          dense
          clearable
          hide-details
          class="mb-3"
        ></v-autocomplete>
        <v-combobox
          v-model="houseData.tags"
          :items="tagsChoices"
          class="mb-3"
          :label="$t('tag')"
          :placeholder="$t('tag')"
          item-text="tag"
          item-value="id"
          multiple
          outlined
          dense
          hide-details
        >
          <template v-slot:selection="{ attrs, item, parent, selected }">
            <v-chip
              v-bind="attrs"
              :input-value="selected"
              small
              label
              color="info"
              outlined
              class="font-weight-semibold disable-min-height--chip mt-1"
            >
              <span class="pr-3">{{ item === Object(item) ? item.tag : item }}</span>
              <v-icon x-small @click="parent.selectItem(item)"> $delete </v-icon>
            </v-chip>
          </template>
        </v-combobox>

        <v-expansion-panels class="mb-3" multiple>
          <v-expansion-panel v-for="(expansionPanel, index) in expansionPanels" :key="index" class="test">
            <v-expansion-panel-header> {{ $t(expansionPanel.title) }} </v-expansion-panel-header>
            <v-expansion-panel-content v-if="index === 0">
              <house-accommodation-details-fields :value="houseData" />
            </v-expansion-panel-content>
            <v-expansion-panel-content v-if="index === 1">
              <house-address-fields :value="houseData" />
            </v-expansion-panel-content>
            <v-expansion-panel-content v-if="index === 2">
              <v-text-field
                v-for="customField in customFields"
                :key="customField.id"
                v-model="customField.field"
                outlined
                dense
                :label="customField.name"
                :placeholder="customField.name"
                hide-details
                maxlength="100"
                class="mb-3"
              ></v-text-field>
            </v-expansion-panel-content>
            <v-expansion-panel-content v-if="index === 3">
              <v-row>
                <v-col v-for="(file, fileIndex) in houseData.files" :key="`House--File-${fileIndex}`" cols="12">
                  <v-card outlined>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <file-pond
                            v-model="file.file"
                            :label-idle="$t('filePondIdle')"
                            :allow-multiple="false"
                            accepted-file-types="application/pdf, image/png, image/jpeg"
                            max-file-size="20MB"
                            max-total-file-size="20MB"
                            @addfile="setUploadedFileName(file)"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="file.name"
                            outlined
                            dense
                            :rules="[validators.required]"
                            :label="$t('name')"
                            :placeholder="$t('name')"
                            maxlength="150"
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-combobox
                            v-model="file.types"
                            :items="fileTypes"
                            :label="$t('type')"
                            :placeholder="$t('type')"
                            item-text="type"
                            item-value="id"
                            multiple
                            outlined
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="10">
                          <v-menu
                            :ref="`fileValidUntilMenu-${fileIndex}`"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                            :close-on-content-click="false"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="file.validUntil"
                                :label="$t('validUntil', { date: '' })"
                                readonly
                                outlined
                                dense
                                clearable
                                v-bind="attrs"
                                hide-details="auto"
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="file.validUntil"
                              :first-day-of-week="1"
                              :locale="$i18n.locale"
                              @input="$refs[`fileValidUntilMenu-${fileIndex}`][0].save($event)"
                            ></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="2" class="d-flex align-center justify-end">
                          <v-tooltip top transition="scroll-y-transition" open-delay="150" color="secondary">
                            <template #activator="{ on, attrs }">
                              <v-btn
                                elevation="0"
                                color="error"
                                icon
                                v-bind="attrs"
                                @click="deleteFileToUpload(houseData.files, fileIndex)"
                                v-on="on"
                              >
                                <v-icon>{{ icons.mdiTrashCanOutline }}</v-icon>
                              </v-btn>
                            </template>
                            <span class="text-xs"> {{ $t('removeFile') }} </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" block @click="addFileToUploadHandler">{{ $t('addFile') }}</v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-row>
          <v-col v-for="(room, index) in houseData.rooms" :key="`Add-Room--${index}`" cols="12">
            <v-card outlined class="card--border-primary">
              <v-card-title>
                <v-row>
                  <v-col cols="12" lg="7">
                    <v-text-field
                      v-model="room.name"
                      :label="$t('roomName')"
                      :placeholder="$t('roomName')"
                      outlined
                      dense
                      maxlength="150"
                      hide-details="auto"
                      :rules="[validators.required]"
                    />
                  </v-col>
                  <v-col cols="12" lg="5">
                    <v-text-field
                      v-model="room.demand"
                      :label="$t('accommodationDemand')"
                      :placeholder="$t('accommodationDemand')"
                      outlined
                      dense
                      type="number"
                      min="1"
                      hide-details="auto"
                      :rules="[
                        validators.minRoomDemand,
                        validators.roomDemandForOccupantsValidator(roomOccupantsCount(room)),
                      ]"
                    >
                      <template #append-outer>
                        <v-tooltip top transition="scroll-y-transition" open-delay="150">
                          <template #activator="{ on, attrs }">
                            <v-btn color="error" x-small v-bind="attrs" @click="deleteRoom(index)" v-on="on">
                              <v-icon class="me-2" size="16">
                                {{ icons.mdiTrashCanOutline }}
                              </v-icon>
                              <span v-t="'delete'" />
                            </v-btn>
                          </template>
                          <span v-t="'deleteRoom'" class="text-xs" />
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <v-btn-toggle v-model="room.occupied" mandatory color="primary" class="mb-3">
                  <v-btn v-t="'employee'" class="text--primary" left outlined />
                  <v-btn v-t="'occupied'" class="text--primary" right outlined />
                </v-btn-toggle>
                <v-autocomplete
                  v-if="!room.occupied"
                  v-model="room.occupants"
                  :items="employeesChoices"
                  outlined
                  chips
                  clearable
                  deletable-chips
                  multiple
                  hide-details="auto"
                  :label="$t('employee')"
                  :placeholder="$t('employee')"
                  :rules="[validators.roomOccupantsCapacityValidator(room.demand)]"
                  class="mb-1"
                />
                <div
                  v-if="!room.occupied"
                  v-for="employeeId in room.occupants"
                  :key="`overlap-error-${index}-${employeeId}`"
                >
                  <div
                    v-if="roomOverlapErrors[index] && roomOverlapErrors[index][employeeId]"
                    class="error--text text-caption mb-2"
                  >
                    {{ roomOverlapErrors[index][employeeId] }}
                  </div>
                </div>
                <v-text-field
                  v-else
                  v-model="room.note"
                  :label="$t('note')"
                  outlined
                  dense
                  maxlength="50"
                  hide-details="auto"
                  class="mb-3"
                />
                <upgrade-alert-wrapper v-if="roomHasEmployees(room)" :form="false" class="mb-3">
                  <template #blocked-features>
                    <v-menu
                      :ref="`roomDateStartMenu-${index}`"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                      :disabled="!$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)"
                    >
                      <template v-slot:activator="activator">
                        <v-text-field
                          v-model="room.dateStart"
                          :disabled="!$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)"
                          :label="$t('accommodationDateStart')"
                          readonly
                          outlined
                          dense
                          v-bind="activator.attrs"
                          hide-details="auto"
                          :rules="[
                            (v) =>
                              !$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES) ||
                              !!v ||
                              $t('accommodationDateStartRequired'),
                            validators.dateRangeValidator(room.dateStart, room.dateEnd),
                          ]"
                          class="mb-3"
                          v-on="activator.on"
                        />
                      </template>
                      <v-date-picker
                        v-model="room.dateStart"
                        :first-day-of-week="1"
                        :locale="$i18n.locale"
                        @input="$refs[`roomDateStartMenu-${index}`][0].save($event)"
                      />
                    </v-menu>
                    <v-menu
                      :ref="`roomDateEndMenu-${index}`"
                      :disabled="!$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="activator">
                        <v-text-field
                          v-model="room.dateEnd"
                          :disabled="!$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)"
                          :label="$t('accommodationDateEnd')"
                          readonly
                          outlined
                          dense
                          clearable
                          v-bind="activator.attrs"
                          hide-details="auto"
                          :rules="[validators.dateRangeValidator(room.dateStart, room.dateEnd)]"
                          class="mb-3"
                          v-on="activator.on"
                        />
                      </template>
                      <v-date-picker
                        v-model="room.dateEnd"
                        :first-day-of-week="1"
                        :locale="$i18n.locale"
                        @input="$refs[`roomDateEndMenu-${index}`][0].save($event)"
                      />
                    </v-menu>
                  </template>
                </upgrade-alert-wrapper>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn v-t="'addRoom'" color="primary" class="mb-6" block @click="addRoom" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row class="overwritten-drawer-actions">
          <v-col cols="12" md="6">
            <v-btn color="primary" class="me-3" type="submit" :disabled="!canSubmit || hasOverlapErrors" block :loading="loading">
              {{ $t('add') }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn v-t="'discard'" color="secondary" outlined type="reset" block @click="resetHouseData" />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-form>
  </v-navigation-drawer>
</template>

<script>
import { required, minRoomDemand, dateRangeValidator, roomOccupantsCapacityValidator, roomDemandForOccupantsValidator } from '@core/utils/validation'

import { mdiClose, mdiTrashCanOutline } from '@mdi/js'
import { ref, watch, computed, nextTick } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import _ from 'lodash'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileEncode from 'filepond-plugin-file-encode'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import houseUtils from '@/utils/houseUtils'
import { ACTIONS, PRIVATE } from '@/plugins/acl/const'
import fileUploadUtils from '@/utils/fileUploadUtils'
import UpgradeAlertWrapper from '@/components/UpgradeAlertWrapper.vue'
import HouseAccommodationDetailsFields from '../house-view/house-bio-panel/HouseAccommodationDetailsFields.vue'
import HouseAddressFields from '../house-view/house-bio-panel/HouseAddressFields.vue'
import ability from '@/plugins/acl/ability'
import {
  buildOccupancyOverlapMessage,
  findFormOccupancyOverlap,
  getEmployeeChoiceLabel,
} from '@/utils/occupancyOverlapUtils'
import { blankHouseAccommodationFields, prepareHouseAccommodationPayload } from '@/utils/houseAccommodationUtils'

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileEncode, FilePondPluginFileValidateSize)

export default {
  components: { UpgradeAlertWrapper, FilePond, HouseAccommodationDetailsFields, HouseAddressFields },
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-user-sidebar-active',
  },
  props: {
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const {
      formatOccupants,

      fetchStatusChoices,
      fetchTagChoices,

      statusChoices,
      tagsChoices,
    } = houseUtils()

    const { setUploadedFileName, parseFilesToCreate, addFileToUpload, deleteFileToUpload } = fileUploadUtils()
    const { fetchFileTypes, fileTypes } = houseUtils()

    const customFields = ref([])
    const employeesChoices = ref([])

    const blankHouseData = {
      name: '',
      description: '',
      status: null,
      street: '',
      zipCode: '',
      houseNumber: '',
      flatNumber: '',
      city: '',
      state: '',
      country: '',
      rooms: [],
      files: [],
      tags: [],
      ...blankHouseAccommodationFields,
    }

    const expansionPanels = [
      {
        title: 'details',
      },
      {
        title: 'address',
      },
      {
        title: 'customFields',
      },
      {
        title: 'files',
      },
    ]

    const { createHouse, getHouseCustomFields, getAllEmployeesChoices, checkEmployeeOccupancyOverlap } = useActions(
      'staff',
      ['createHouse', 'getHouseCustomFields', 'getAllEmployeesChoices', 'checkEmployeeOccupancyOverlap'],
    )

    const loading = ref(false)
    const roomOverlapErrors = ref({})
    const shineFeaturesEnabled = ability.can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)

    const valid = ref(false)
    const form = ref(null)

    const validate = () => {
      form.value.validate()
    }

    const resetForm = () => {
      form.value.reset()
    }

    const houseData = ref(_.cloneDeep(blankHouseData))

    const addRoom = () => {
      houseData.value.rooms.push({
        name: '',
        demand: 1,
        occupants: [],
        dateStart: null,
        dateEnd: null,
        occupied: 0,
        note: '',
      })
    }

    const roomOccupantsCount = (room) => (room.occupants && room.occupants.length) || 0

    const roomHasEmployees = (room) => !room.occupied && roomOccupantsCount(room) > 0

    const isRoomComplete = (room) => {
      if (!room.name?.trim()) return false

      const demand = parseInt(room.demand, 10) || 0
      if (demand < 1) return false

      if (!room.occupied && roomOccupantsCount(room) > demand) return false

      return true
    }

    const canSubmit = computed(() => {
      if (!houseData.value.name?.trim()) return false
      if (houseData.value.rooms.length === 0) return true

      return houseData.value.rooms.every(isRoomComplete)
    })

    const deleteRoom = (index) => {
      houseData.value.rooms.splice(index, 1)
      roomOverlapErrors.value = {}
      refreshAllOverlapErrors()
    }

    const clearOverlapErrors = () => {
      roomOverlapErrors.value = {}
    }

    const checkEmployeeOverlap = async (roomIndex, employeeId) => {
      const room = houseData.value.rooms[roomIndex]
      if (!room || room.occupied || !employeeId) return null

      if (shineFeaturesEnabled && !room.dateStart) return null

      const formConflict = findFormOccupancyOverlap({
        rooms: houseData.value.rooms,
        currentRoomIndex: roomIndex,
        employeeId,
        dateStart: room.dateStart,
        dateEnd: room.dateEnd,
        employeesChoices: employeesChoices.value,
        houseName: houseData.value.name,
        shineFeaturesEnabled,
      })
      if (formConflict) return formConflict

      const conflict = await checkEmployeeOccupancyOverlap({
        employee: employeeId,
        date_start: room.dateStart || new Date().toISOString().slice(0, 10),
        date_end: room.dateEnd || undefined,
      })
      if (!conflict) return null

      return buildOccupancyOverlapMessage({
        employeeName: conflict.employeeName || getEmployeeChoiceLabel(employeesChoices.value, employeeId),
        houseName: conflict.houseName,
        roomName: conflict.roomName,
      })
    }

    const refreshRoomOverlapErrors = async (roomIndex) => {
      const room = houseData.value.rooms[roomIndex]
      if (!room || room.occupied) {
        roomOverlapErrors.value = {
          ...roomOverlapErrors.value,
          [roomIndex]: {},
        }
        return
      }

      const errors = {}
      const checks = (room.occupants || []).map(async (employeeId) => {
        const message = await checkEmployeeOverlap(roomIndex, employeeId)
        if (message) errors[employeeId] = message
      })
      await Promise.all(checks)

      roomOverlapErrors.value = {
        ...roomOverlapErrors.value,
        [roomIndex]: errors,
      }
    }

    const refreshAllOverlapErrorsNow = async () => {
      await Promise.all(houseData.value.rooms.map((_, roomIndex) => refreshRoomOverlapErrors(roomIndex)))
    }

    const refreshAllOverlapErrors = _.debounce(refreshAllOverlapErrorsNow, 300)

    const hasOverlapErrors = computed(() =>
      Object.values(roomOverlapErrors.value).some((roomErrors) => Object.keys(roomErrors).length > 0),
    )

    const resetHouseData = () => {
      houseData.value = _.cloneDeep(blankHouseData)
      clearOverlapErrors()
      resetForm()
      emit('update:is-add-new-user-sidebar-active', false)
    }

    const onSubmit = async () => {
      await refreshAllOverlapErrorsNow()
      if (canSubmit.value && !hasOverlapErrors.value && valid.value) {
        loading.value = true
        houseData.value.rooms = houseData.value.rooms.map((room) => {
          return {
            ...room,
            occupants: formatOccupants(room),
          }
        })
        houseData.value.files = parseFilesToCreate(houseData.value.files)
        await createHouse(
          prepareHouseAccommodationPayload({
            ...houseData.value,
            tags: houseData.value.tags.map((tag) => {
              return _.isObject(tag) ? tag : { tag }
            }),
            customFieldsValues: customFields.value.map((field) => {
              return { customField: field.id, field: field.field }
            }),
          }),
        )
        loading.value = false
        resetHouseData()
        emit('refetch-data')
      } else {
        validate()
      }
    }

    const addFileToUploadHandler = () => {
      addFileToUpload(houseData.value.files, { validUntil: true, types: true })
    }

    watch(
      () => houseData.value.rooms,
      () => {
        refreshAllOverlapErrors()
        nextTick(() => {
          if (form.value) {
            form.value.validate()
          }
        })
      },
      { deep: true },
    )

    watch(
      () => houseData.value.name,
      () => {
        refreshAllOverlapErrors()
      },
    )

    watch(
      () => props.isAddNewUserSidebarActive,
      async (newVal) => {
        if (newVal) {
          await fetchStatusChoices()
          await fetchFileTypes()
          await fetchTagChoices()
          customFields.value = await getHouseCustomFields()
          employeesChoices.value = await getAllEmployeesChoices()
        }
      },
    )

    return {
      addRoom,
      deleteRoom,

      resetHouseData,
      onSubmit,

      setUploadedFileName,
      addFileToUploadHandler,
      deleteFileToUpload,
      fileTypes,

      form,
      loading,
      houseData,
      valid,
      canSubmit,
      roomHasEmployees,
      roomOccupantsCount,
      employeesChoices,
      roomOverlapErrors,
      hasOverlapErrors,
      statusChoices,
      expansionPanels,
      customFields,

      tagsChoices,

      // validation
      validators: {
        required,
        minRoomDemand,
        dateRangeValidator,
        roomOccupantsCapacityValidator,
        roomDemandForOccupantsValidator,
      },
      icons: {
        mdiClose,
        mdiTrashCanOutline,
      },
      ACTIONS,
      PRIVATE,
    }
  },
}
</script>
